/**
 * Der Frontbalken (GAME_DESIGN Abschnitt 13).
 *
 * Eine schmale Leiste unter der Uhr, die vier Dinge zeigt:
 *
 *   - wo die Front auf der gesamten Lane steht,
 *   - wie weit die eigene Armee Raum haelt,
 *   - wie weit die gegnerische,
 *   - den umkaempften Bereich dazwischen.
 *
 * Er ist ausdruecklich **keine Minimap** - eine klassische MOBA-Minimap ist
 * nicht vorgesehen. Er beantwortet genau eine Frage: Gewinne oder verliere
 * ich gerade Raum?
 *
 * Immer aus der eigenen Sicht gelesen: links das eigene Anchor, rechts das
 * des Gegners. Die Zahlen im Zustand zaehlen dagegen immer von Blau aus -
 * fuer Orange wird hier einmal gespiegelt, damit beide Spieler dasselbe Bild
 * haben. Ohne diese Drehung saehe einer von beiden seine Front auf der
 * falschen Seite wachsen.
 */

import { useEffect, useState } from 'react';
import { ANCHOR_HIT_WARNING_MS, FRONT_PRESSURE_WARNING_MS } from '@dueline/game-data';
import {
  druckGruende,
  stepDruckWarnung,
  KEINE_DRUCKWARNUNG,
  type DruckWarnung,
  type FrontAnteil,
} from '@dueline/game-rules';
import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';

/**
 * Haelt die Druckwarnung fuer ein paar Sekunden und laesst sie dann gehen
 * (Produktentscheidung vom 12.10.2026).
 *
 * WANN sie zu sehen ist, entscheidet die Regel in game-rules - sie ist
 * ohne Bild pruefbar. Hier steht nur, was React dafuer braucht: ein
 * Gedaechtnis zwischen zwei Bildern und eine Uhr, die das Verschwinden
 * ausloest.
 *
 * Der Wecker ist noetig, weil die Oberflaeche sich sonst erst beim
 * naechsten Zustandswechsel neu zeichnet - die Warnung bliebe stehen,
 * bis zufaellig etwas anderes passiert.
 */
function useDruckWarnung(unterDruck: boolean): boolean {
  const [warnung, setWarnung] = useState<{ sichtbar: boolean; zustand: DruckWarnung }>({
    sichtbar: false,
    zustand: KEINE_DRUCKWARNUNG,
  });

  /*
   * Zwei Anlaesse, die Regel zu fragen - mehr gibt es nicht:
   *
   *   1. Der Druck hat sich geaendert (der Effekt haengt daran).
   *   2. Die Anzeigedauer ist abgelaufen (der Wecker).
   *
   * Ohne den Wecker bliebe die Warnung stehen, bis zufaellig etwas anderes
   * die Oberflaeche neu zeichnet.
   */
  useEffect(() => {
    const takt = () => {
      // Die Uhr wird HIER gelesen und nicht im Updater: Der muss bei
      // gleicher Eingabe gleich herauskommen.
      const jetztMs = Date.now();
      setWarnung((vorher) =>
        stepDruckWarnung({ unterDruck, jetztMs, zustand: vorher.zustand }),
      );
    };

    takt();
    const uhr = setTimeout(takt, FRONT_PRESSURE_WARNING_MS);
    return () => clearTimeout(uhr);
  }, [unterDruck]);

  return warnung.sichtbar;
}

/** Dreht einen Anteil in die eigene Sicht: 0 ist immer das eigene Anchor. */
function ausEigenerSicht(anteil: number, eigeneSeite: string): number {
  return eigeneSeite === 'blue' ? anteil : 1 - anteil;
}

function prozent(anteil: number): string {
  return `${Math.round(Math.max(0, Math.min(1, anteil)) * 100)}%`;
}

export function FrontlineBar({ snapshot }: { snapshot: MatchSnapshot }) {
  // Vor dem Startschuss haelt niemand Raum - dann waere der Balken nur Deko.
  const eigene: FrontAnteil =
    snapshot.ownSide === 'blue' ? snapshot.frontlines.blue : snapshot.frontlines.orange;
  const gegnerische: FrontAnteil =
    snapshot.ownSide === 'blue' ? snapshot.frontlines.orange : snapshot.frontlines.blue;

  /*
   * Der Druck wird VOR dem vorzeitigen Ausstieg gerechnet.
   *
   * Nicht aus Ordnungsliebe: useDruckWarnung ist ein Haken, und ein Haken
   * muss in jedem Durchlauf drankommen. Haengte er hinter "return null",
   * fiele er vor dem Startschuss weg und taeuchte danach auf - React
   * verliert dabei die Zuordnung.
   */
  const gruende = druckGruende({
    side: snapshot.ownSide,
    gegnerischeFront: gegnerische,
    msSeitAnchorTreffer: snapshot.msSincePortalDamage,
    trefferFensterMs: ANCHOR_HIT_WARNING_MS,
  });
  const warnung = useDruckWarnung(gruende.length > 0);

  if (!eigene.aktiv && !gegnerische.aktiv) return null;

  const meine = ausEigenerSicht(eigene.anteil, snapshot.ownSide);
  const seine = ausEigenerSicht(gegnerische.anteil, snapshot.ownSide);

  /*
   * Der umkaempfte Bereich liegt zwischen den beiden Linien. Welche davon
   * weiter links liegt, haengt davon ab, wer gerade drueckt - deshalb nicht
   * "von meiner bis zu seiner", sondern schlicht die kleinere bis zur
   * groesseren.
   */
  const von = Math.min(meine, seine);
  const bis = Math.max(meine, seine);


  return (
    <div className="dl-front" role="group" aria-label={t.frontline.label}>
      <div className="dl-front__track">
        {(eigene.aktiv || gegnerische.aktiv) && (
          <div
            className="dl-front__contested"
            style={{ left: prozent(von), width: prozent(bis - von) }}
          />
        )}
        {eigene.aktiv && (
          <div className="dl-front__mark dl-front__mark--own" style={{ left: prozent(meine) }} />
        )}
        {gegnerische.aktiv && (
          <div className="dl-front__mark dl-front__mark--enemy" style={{ left: prozent(seine) }} />
        )}
      </div>

      {/*
        Produktentscheidung vom 11.10.2026: Die Zahl unter dem Balken
        ("FRONT 24%", "ENEMY FRONT 24%") ist weg - bewusste Abweichung von
        Grundsatz 40 (kein Wert nur ueber Farbe). Der Grund: Der Balken soll
        ruhig direkt unter der Uhr sitzen, und die Lage liest man an der
        POSITION der Marken ab, nicht an ihrer Farbe.
      */}

      {/*
        Seit dem 13.10.2026 nur die Warnung selbst, ohne den Grund darunter
        ("pushed back", "Anchor hit" ...) - Rueckmeldung: "dass da nur das
        steht". Die Gruende entscheiden weiter, OB sie erscheint.
      */}
      {warnung && (
        <span className="dl-front__pressure" role="alert">
          {t.frontline.pressure}
        </span>
      )}
    </div>
  );
}
